import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, STORE_SLUG } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Share2, ShoppingCart, CalendarDays, MapPin, Clock, Minus, Plus, Shirt } from "lucide-react";

const GOLD = "var(--gv-blue-bright)";

function ProductCard({ p, onAdd }) {
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  return (
    <div className="border border-white/10 bg-[#141518] p-5 flex flex-col gap-4" data-testid={`sn-product-${p.size_category}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-3xl leading-none">{p.category_label}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mt-1">Performance Tee • Black</p>
        </div>
        <p className="font-display text-4xl leading-none" style={{ color: GOLD }} data-testid={`sn-price-${p.size_category}`}>
          ${Number(p.flat_price).toFixed(0)}
        </p>
      </div>

      <p className="text-xs text-gray-400">{p.size_note}</p>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-bold mb-2">Size</p>
        <div className="flex flex-wrap gap-2">
          {(p.sizes || []).map((s) => (
            <button key={s} onClick={() => setSize(s)} data-testid={`sn-size-${p.size_category}-${s.replace(/\s/g, "-")}`}
              className={`px-3 py-2 text-xs font-bold border transition-colors ${size === s ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-300 hover:border-white/30"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-auto">
        <div className="flex items-center border border-white/10">
          <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid={`sn-qty-minus-${p.size_category}`} className="p-2"><Minus className="w-3 h-3" /></button>
          <span className="w-8 text-center text-sm font-bold" data-testid={`sn-qty-${p.size_category}`}>{qty}</span>
          <button onClick={() => setQty(qty + 1)} data-testid={`sn-qty-plus-${p.size_category}`} className="p-2"><Plus className="w-3 h-3" /></button>
        </div>
        <button onClick={() => { if (!size) return toast.error(`Choose a ${p.category_label} size`); onAdd(p, size, qty); setSize(""); setQty(1); }}
          data-testid={`sn-add-${p.size_category}`}
          className="flex-1 flex items-center justify-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-xs py-3 transition-colors">
          <ShoppingCart className="w-4 h-4" /> Add to Cart
        </button>
      </div>
    </div>
  );
}

export default function SeniorNight() {
  const { playerId } = useParams();
  const { addItem, count } = useCart();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/stores/${STORE_SLUG}/players/${playerId}`)
      .then((r) => setPage(r.data))
      .catch(() => setErr(true));
  }, [playerId]);

  useEffect(() => {
    if (page?.page_title) document.title = page.page_title;
  }, [page]);

  if (err) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-gray-400" data-testid="sn-not-found">This player page was not found.</div>;
  if (!page) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-gray-500">Loading…</div>;

  const share = async () => {
    const url = window.location.href;
    const text = `${page.player_name} — ${page.event_name}, ${page.event_date}. Order a shirt:`;
    if (navigator.share) {
      try { await navigator.share({ title: page.page_title, text, url }); return; } catch { /* dismissed */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it with family & friends!"); }
    catch { toast.error("Could not copy the link"); }
  };

  const onAdd = (p, size, qty) => {
    addItem({
      product_id: p.id, product_name: p.name, size, quantity: qty,
      back_option: "front_only", garment_color: p.garment_color || "Black",
      front_image: p.front_image, back_image: p.back_image,
      unit_price: Number(p.flat_price),
      player_id: p.player_id, size_category: p.size_category, category_label: p.category_label,
    });
    toast.success(`${p.category_label} ${size} added to cart`);
  };

  return (
    <div data-testid="senior-night-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: `url(${page.artwork_image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: GOLD }}>Senior Night 2026</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mt-2" data-testid="sn-player-name">
              SASHA<br /><span style={{ color: GOLD }}>HUGHES</span>
            </h1>
            <p className="text-base text-gray-300 mt-4 max-w-lg" data-testid="sn-bio">{page.bio}</p>
            <div className="flex flex-wrap gap-3 mt-6 text-sm">
              <span className="flex items-center gap-2 border border-white/10 px-3 py-2" data-testid="sn-event-date">
                <CalendarDays className="w-4 h-4" style={{ color: GOLD }} /> {page.event_date}
              </span>
              <span className="flex items-center gap-2 border border-white/10 px-3 py-2 text-gray-300">
                <MapPin className="w-4 h-4" style={{ color: GOLD }} /> {page.event_name}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href="#order" data-testid="sn-order-cta"
                className="bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-6 py-3 transition-colors">
                Order a Shirt
              </a>
              <button onClick={share} data-testid="sn-share-btn"
                className="flex items-center gap-2 border border-white/20 hover:border-[var(--gv-blue)] font-bold uppercase tracking-widest text-sm px-6 py-3 transition-colors">
                <Share2 className="w-4 h-4" /> Share This Page
              </button>
            </div>
          </motion.div>
          <motion.img initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
            src={page.hero_image} alt="Sasha Hughes Senior Night shirt front and back" data-testid="sn-hero-image"
            className="w-full max-w-md mx-auto border border-white/10" />
        </div>
      </section>

      {/* Deadline / pickup strip */}
      <section className="bg-[#141518] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 grid sm:grid-cols-2 gap-4 text-sm">
          <p className="flex items-start gap-2 text-gray-300" data-testid="sn-deadline"><Clock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />{page.order_deadline}</p>
          <p className="flex items-start gap-2 text-gray-300" data-testid="sn-pickup-note"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />{page.pickup_note}</p>
        </div>
      </section>

      {/* Order */}
      <section id="order" className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
          <h2 className="font-display text-4xl sm:text-5xl">ORDER YOUR SHIRT</h2>
          {count > 0 && <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: GOLD }} data-testid="sn-cart-count">{count} in cart</span>}
        </div>
        <p className="text-sm text-gray-400 max-w-2xl mb-2 flex items-start gap-2">
          <Shirt className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />{page.fabric_note}
        </p>
        <p className="text-xs text-gray-500 max-w-2xl mb-8">
          Supplier size charts and exact measurements are being finalised — placeholder sizing guidance is shown on each category below.
          Adult, youth and toddler shirts are separate products and can all be added to the same cart.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          {(page.products || []).map((p) => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}
        </div>
      </section>

      {/* Stats + artwork */}
      <section className="border-t border-white/10 bg-[#0d0e10]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display text-4xl mb-3">SASHA'S CAREER STATS</h2>
            <p className="text-sm text-gray-400 mb-5">Four years in a War Eagles jersey. (Shown here for the Senior Night celebration — the stat graphic is not printed on the shirt.)</p>
            <img src={page.stats_image} alt="Sasha Hughes career stats graphic" data-testid="sn-stats-image" className="w-full border border-white/10" />
          </div>
          <div>
            <h2 className="font-display text-4xl mb-3">THE ARTWORK</h2>
            <p className="text-sm text-gray-400 mb-5">Custom Dad Jerzeys Senior Night design on the front. The back has the GCA eagle with <span className="text-white font-bold">#{page.jersey_number} HUGHES</span> — no stat lines on the shirt.</p>
            <img src={page.artwork_image} alt="Sasha Hughes Senior Night front artwork" data-testid="sn-artwork-image" className="w-full border border-white/10 bg-black" />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-center">
        <button onClick={share} data-testid="sn-share-bottom-btn"
          className="inline-flex items-center gap-2 border border-white/20 hover:border-[var(--gv-blue)] font-bold uppercase tracking-widest text-sm px-6 py-3 transition-colors">
          <Share2 className="w-4 h-4" /> Share Sasha's Page
        </button>
        <p className="text-xs text-gray-500 mt-4">Official team store powered by <span className="text-white font-bold">Dad Jerzeys</span></p>
      </section>
    </div>
  );
}
